import {useMemo} from 'react';
import styled from 'styled-components';
import {LoadingBar} from '@/components/LoadingBar';
import {Typography} from '@/components/Typography';
import {MEDALS} from '@/constants';
import {DataSourceItem, PopulationCompare} from '@/map/@types';
import {useMapDataSource} from '@/map/api';
import {Flexbox, FlexFullGrow, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {withComma} from '@/utils';
import {
  GrayBorderBox, GrayBox, GrayCenterBox, SectionBlock, SectionSubTitle, SectionTitle, SubValueText,
  ValueText
} from '../../StyledComponents';
import {EMPTY_ITEM_TYPES, HOUSE_POPULATION_CHART_INFO} from '../constants';
import {EmptyItem} from '../EmptyItem';
import {PopulationDataProps, SectionPropsBase} from '../interface';
import {getHouseholdCompareData} from '../utils';

interface SectionProps extends SectionPropsBase {
  data?: Item[];
}

type Item = {
  idx: number;
  total: number;
  percentage: Array<{
    key: string;
    percent: number;
    count: number;
  }>;
} & PopulationCompare;

export const HousePopulationTypeCompare = ({
  data: rawdata,
  ...props
}: PopulationDataProps) => {
  const {data: dataSource} = useMapDataSource();
  const metadata = useMemo(
    () => dataSource?.find((source) => source.key === 'houseHolds'),
    [dataSource]
  );

  const data = getHouseholdCompareData(rawdata);

  return (
    <>
      <TotalRanking data={data} {...props} metadata={metadata} />
      <PopulationPercent data={data} {...props} metadata={metadata} />
    </>
  );
};

const TotalRanking = ({
  data,
  error,
  isPending,
  metadata,
}: SectionProps & {
  metadata?: DataSourceItem;
}) => {
  return (
    <SectionBlock>
      <SectionTitle>배후세대 수</SectionTitle>
      <br />
      {!!metadata && (
        <SectionSubTitle>{`${metadata.source}∙${
          metadata.baseDate || ''
        }`}</SectionSubTitle>
      )}
      <PaddingBox top={16}>
        {isPending ? (
          <GrayCenterBox>
            <LoadingBar color={palette.textSecondary} />
          </GrayCenterBox>
        ) : error ? (
          <Typography variant="medium12" color={palette.textSecondary}>
            분석 실패
          </Typography>
        ) : !data ? (
          <EmptyItem type={EMPTY_ITEM_TYPES.Population} />
        ) : (
          <TotalRankingBox items={data} />
        )}
      </PaddingBox>
    </SectionBlock>
  );
};

const TotalRankingBox = ({items}: {items: Item[]}) => {
  const ranking = [...items].sort((a, b) => b.total - a.total);

  return (
    <>
      <Typography variant="medium14">
        <Typography color={palette.primary500} fontWeight="700">
          총 배후세대 수
        </Typography>
        가 높은 상권은?
      </Typography>
      <PaddingBox top={10}>
        <GrayBox>
          <RankingBox>
            {ranking.map((i, ranking) => (
              <Rank key={`TotalRanking-Rank-${i.idx}`}>
                {MEDALS[ranking]}
                <Typography variant="medium14" color={palette.textPrimary}>
                  비교 상권 {i.idx + 1}
                </Typography>
                <FlexFullGrow />
                <div>
                  <ValueText>{withComma(i.total)}</ValueText>{' '}
                  <SubValueText>세대</SubValueText>
                </div>
              </Rank>
            ))}
          </RankingBox>
        </GrayBox>
      </PaddingBox>
    </>
  );
};

const PopulationPercent = ({
  data,
  error,
  isPending,
  metadata,
}: SectionProps & {
  metadata?: DataSourceItem;
}) => {
  return (
    <SectionBlock>
      <SectionTitle>배후세대 비중</SectionTitle>
      <br />
      {!!metadata && (
        <SectionSubTitle>{`${metadata.source}∙${
          metadata.baseDate || ''
        }`}</SectionSubTitle>
      )}
      <PaddingBox top={16}>
        <PercentBoxWrapper>
          {isPending ? (
            <GrayCenterBox>
              <LoadingBar color={palette.textSecondary} />
            </GrayCenterBox>
          ) : error ? (
            <Typography variant="medium12" color={palette.textSecondary}>
              분석 실패
            </Typography>
          ) : !data ? (
            <EmptyItem type={EMPTY_ITEM_TYPES.Population} />
          ) : (
            data.map((item: Item) => (
              <PopulationPercentItem
                item={item}
                key={`PopulationPercent-Item-${item.idx}`}
              />
            ))
          )}
        </PercentBoxWrapper>
      </PaddingBox>
    </SectionBlock>
  );
};

const PopulationPercentItem = ({item}: {item: Item}) => {
  if (!item.total) {
  }
  return (
    <GrayBorderBox>
      <Typography variant="medium14" color={palette.textSecondary}>
        비교 상권 {item.idx + 1}
      </Typography>
      {!item.total ? (
        <PaddingBox top={12}>
          <Typography variant="medium12" color={palette.textSecondary}>
            상권 내 배후세대가 없습니다.
          </Typography>
        </PaddingBox>
      ) : (
        <PaddingBox top={12}>
          <ChartWrapper>
            {item.percentage.map((i) => (
              <Bar
                style={{
                  flexGrow: i.percent,
                  backgroundColor: HOUSE_POPULATION_CHART_INFO[i.key].color,
                }}
                key={i.key}
              />
            ))}
          </ChartWrapper>
          <PaddingBox top={10}>
            <RankingBox>
              {[...item.percentage]
                .sort((a, b) => b.percent - a.percent)
                .map((i, idx) => (
                  <Rank key={`PopulationPercent-Rank-${i.key}`}>
                    <ValueText>{idx + 1}.</ValueText>
                    <ColorDot
                      style={{
                        backgroundColor:
                          HOUSE_POPULATION_CHART_INFO[i.key].color,
                      }}
                    />{' '}
                    <Typography
                      variant="medium12"
                      color={palette.textSecondary}
                    >
                      {HOUSE_POPULATION_CHART_INFO[i.key].label}
                    </Typography>
                    <FlexFullGrow />
                    <div>
                      <ValueText>{(i.percent * 100).toFixed(1)}%</ValueText>
                      <SubValueText>({withComma(i.count)} 세대)</SubValueText>
                    </div>
                  </Rank>
                ))}
            </RankingBox>
          </PaddingBox>
        </PaddingBox>
      )}
    </GrayBorderBox>
  );
};

const ChartWrapper = styled.div`
  display: flex;
  height: 8px;
  border-radius: 10px;
  overflow: hidden;
  gap: 2px;
`;

const Bar = styled.div`
  height: 100%;
  flex-basis: 0;
`;

const ColorDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 100%;
`;

const PercentBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Rank = styled(Flexbox)`
  gap: 4px;
`;

const RankingBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
